import { memo, useMemo, useRef, useState } from "react";
import { Panel } from "primereact/panel";
import { MultiSelect } from "primereact/multiselect";
import { InputNumber } from "primereact/inputnumber";
import { SelectButton } from "primereact/selectbutton";
import { Checkbox } from "primereact/checkbox";
import { Button } from "primereact/button";
import { useReactToPrint } from "react-to-print";
import { useReport } from "../../context/report";

const OPERADORES = [
    { label: "≥", value: "gte" },
    { label: ">", value: "gt" },
    { label: "≤", value: "lte" },
    { label: "<", value: "lt" },
    { label: "=", value: "eq" },
];

const EXIBIR = [
    { label: "Trama", value: "trama" },
    { label: "Urdume", value: "urdume" },
    { label: "Trama / Urdume", value: "ambos" },
];

const CORES_TURMA = { A: "#c00000", B: "#ffc000", C: "#ff0000", D: "#ff33cc" };

const compara = (valor, operador, limite) => {
    if (limite === null || limite === undefined) return true;
    switch (operador) {
        case "gt": return valor > limite;
        case "gte": return valor >= limite;
        case "lt": return valor < limite;
        case "lte": return valor <= limite;
        case "eq": return valor === limite;
        default: return true;
    }
};

// "dd/mm/yy" -> Date
const parseData = (str) => {
    const m = /^(\d{2})\/(\d{2})\/(\d{2,4})$/.exec(String(str).trim());
    if (!m) return null;
    const ano = Number(m[3]) < 100 ? 2000 + Number(m[3]) : Number(m[3]);
    return new Date(ano, Number(m[2]) - 1, Number(m[1]));
};

const rotuloData = (ts) => {
    const d = new Date(ts);
    return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}`;
};

export default memo(function QuebrasDiaArtigo() {
    const { data } = useReport();

    const [artigos, setArtigos] = useState([]);

    const [tramaOp, setTramaOp] = useState("gte");
    const [tramaLimite, setTramaLimite] = useState(15);
    const [urdumeOp, setUrdumeOp] = useState("gte");
    const [urdumeLimite, setUrdumeLimite] = useState(null);

    const [exibir, setExibir] = useState("trama");
    const [ocultarVazios, setOcultarVazios] = useState(true);
    const [turnoOrdenado, setTurnoOrdenado] = useState(null); // 'ultimo' | 'penultimo' | null

    const tabelaRef = useRef(null);
    const [printing, setPrinting] = useState(false);

    const handlePrint = useReactToPrint({
        content: () => tabelaRef.current,
        pageStyle: "@page { size: A3 landscape; margin: 8mm; } * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }",
        onBeforeGetContent: () =>
            new Promise((resolve) => {
                setPrinting(true);
                setTimeout(resolve, 500);
            }),
        onAfterPrint: () => setPrinting(false),
    });

    // Registros brutos vindos do backend (sem as linhas "Total")
    const registros = useMemo(() => {
        if (!data?.datatable) return [];
        const lista = [];
        data.datatable.forEach((r) => {
            if (r.turma === "Total") return;
            const date = parseData(r.data);
            if (!date) return;
            lista.push({
                tear: r.tear,
                artigo: r.artigo === null || r.artigo === undefined || r.artigo === "" ? "-" : String(r.artigo),
                turma: r.turma,
                date,
                ts: date.getTime(),
                trama: Number(r.trama) || 0,
                urdume: Number(r.urdume) || 0,
            });
        });
        return lista;
    }, [data]);

    const artigoOptions = useMemo(
        () =>
            [...new Set(registros.map((r) => r.artigo))]
                .sort((a, b) => a.localeCompare(b, "pt-BR", { numeric: true }))
                .map((a) => ({ label: a, value: a })),
        [registros]
    );

    const coresTurma = useMemo(() => {
        const mapa = { ...CORES_TURMA };
        (data?.chart || []).forEach((c) => {
            if (c.turma && c.color) mapa[c.turma] = c.color;
        });
        return mapa;
    }, [data]);

    const {
        linhas,
        colunas,
        filtroAtivo,
        ultimoTurno,
        penultimoTurno,
        turnoAlvo,
        top5Teares,
    } = useMemo(() => {
        const base = registros.filter(
            (r) => !artigos || artigos.length === 0 || artigos.includes(r.artigo)
        );

        // agrupa por tear + artigo, somando quebras de cada (data, turma)
        const mapa = new Map();
        base.forEach((r) => {
            const chave = `${r.tear}|${r.artigo}`;
            if (!mapa.has(chave)) mapa.set(chave, { tear: r.tear, artigo: r.artigo, celulas: {} });
            const cel = mapa.get(chave).celulas;
            const k = `${r.ts}|${r.turma}`;
            if (!cel[k]) cel[k] = { trama: 0, urdume: 0 };
            cel[k].trama += r.trama;
            cel[k].urdume += r.urdume;
        });

        // Cada filtro age somente na sua própria métrica
        const ativoT = tramaLimite !== null && tramaLimite !== undefined;
        const ativoU = urdumeLimite !== null && urdumeLimite !== undefined;
        const okT = (c) => !ativoT || compara(c.trama, tramaOp, tramaLimite);
        const okU = (c) => !ativoU || compara(c.urdume, urdumeOp, urdumeLimite);

        const passa = (c) => {
            if (exibir === "trama") return okT(c);
            if (exibir === "urdume") return okU(c);
            if (!ativoT && !ativoU) return true;
            return (ativoT && okT(c)) || (ativoU && okU(c));
        };

        let lista = [...mapa.values()];

        if (ocultarVazios) {
            lista = lista.filter((l) => Object.values(l.celulas).some(passa));
        }

        // turmas que efetivamente trabalharam em cada dia
        const turmasPorDia = new Map();
        base.forEach((r) => {
            if (!turmasPorDia.has(r.ts)) turmasPorDia.set(r.ts, new Set());
            turmasPorDia.get(r.ts).add(r.turma);
        });

        let todasDatas;
        if (ocultarVazios) {
            const set = new Set();
            lista.forEach((l) =>
                Object.entries(l.celulas).forEach(([k, c]) => {
                    if (passa(c)) set.add(Number(k.split("|")[0]));
                })
            );
            todasDatas = [...set];
        } else {
            todasDatas = [...new Set(base.map((r) => r.ts))];
        }
        todasDatas.sort((a, b) => a - b);

        const colunasPorDia = todasDatas.map((ts) => ({
            ts,
            turmas: [...(turmasPorDia.get(ts) || [])].sort(),
        }));

        // Lista cronológica de todos os turnos existentes
        const turnosCronologicos = [];
        colunasPorDia.forEach((col) => {
            col.turmas.forEach((t) => {
                turnosCronologicos.push({
                    ts: col.ts,
                    turma: t,
                    chave: `${col.ts}|${t}`,
                    rotulo: `${rotuloData(col.ts)} - Turma ${t}`,
                });
            });
        });

        const ult = turnosCronologicos.length > 0 ? turnosCronologicos[turnosCronologicos.length - 1] : null;
        const penult = turnosCronologicos.length > 1 ? turnosCronologicos[turnosCronologicos.length - 2] : null;

        const alvo = turnoOrdenado === "ultimo" ? ult : turnoOrdenado === "penultimo" ? penult : null;
        const chaveAlvo = alvo?.chave || null;

        const getQuebra = (item) => {
            if (!chaveAlvo || !item.celulas) return 0;
            const c = item.celulas[chaveAlvo];
            if (!c) return 0;
            if (exibir === "trama") return c.trama || 0;
            if (exibir === "urdume") return c.urdume || 0;
            return (c.trama || 0) + (c.urdume || 0);
        };

        // Ordenação
        if (chaveAlvo) {
            const quebrasPorTear = new Map();
            lista.forEach((item) => {
                const q = getQuebra(item);
                const atual = quebrasPorTear.get(item.tear);
                if (atual === undefined || q > atual) quebrasPorTear.set(item.tear, q);
            });

            lista.sort((a, b) => {
                const qA = quebrasPorTear.get(a.tear) || 0;
                const qB = quebrasPorTear.get(b.tear) || 0;
                if (qB !== qA) return qB - qA;
                if (Number(a.tear) !== Number(b.tear)) return Number(a.tear) - Number(b.tear);
                const subA = getQuebra(a);
                const subB = getQuebra(b);
                if (subB !== subA) return subB - subA;
                return a.artigo.localeCompare(b.artigo, "pt-BR", { numeric: true });
            });
        } else {
            lista.sort(
                (a, b) =>
                    Number(a.tear) - Number(b.tear) ||
                    a.artigo.localeCompare(b.artigo, "pt-BR", { numeric: true })
            );
        }

        // Identifica os primeiros 5 teares com quebras no turno selecionado para destacar
        const top5 = new Set();
        if (chaveAlvo) {
            const tearesVistos = new Set();
            for (const item of lista) {
                const q = getQuebra(item);
                if (q > 0 && !tearesVistos.has(item.tear)) {
                    tearesVistos.add(item.tear);
                    top5.add(item.tear);
                    if (top5.size >= 5) break;
                }
            }
        }

        // marca cada célula com a flag "passa" e calcula rowSpan do tear
        const resultado = lista.map((l, i) => ({
            ...l,
            celulas: Object.fromEntries(
                Object.entries(l.celulas).map(([k, c]) => [
                    k,
                    { ...c, ok: passa(c), okT: okT(c), okU: okU(c) },
                ])
            ),
            primeiroDoTear: i === 0 || lista[i - 1].tear !== l.tear,
        }));
        resultado.forEach((l, i) => {
            if (!l.primeiroDoTear) return;
            let n = 1;
            while (i + n < resultado.length && resultado[i + n].tear === l.tear) n++;
            l.rowSpan = n;
        });

        return {
            linhas: resultado,
            colunas: colunasPorDia,
            filtroAtivo:
                exibir === "trama" ? ativoT : exibir === "urdume" ? ativoU : ativoT || ativoU,
            ultimoTurno: ult,
            penultimoTurno: penult,
            turnoAlvo: alvo,
            top5Teares: top5,
        };
    }, [registros, artigos, exibir, tramaOp, tramaLimite, urdumeOp, urdumeLimite, ocultarVazios, turnoOrdenado]);

    const limpar = () => {
        setArtigos([]);
        setTramaOp("gte");
        setTramaLimite(null);
        setUrdumeOp("gte");
        setUrdumeLimite(null);
        setTurnoOrdenado(null);
    };

    const renderCelula = (c) => {
        if (!c || !c.ok) return "";
        if (exibir === "trama") return c.trama;
        if (exibir === "urdume") return c.urdume;
        return `${c.okT ? c.trama : "–"} / ${c.okU ? c.urdume : "–"}`;
    };

    const opcoesClassificacao = useMemo(() => {
        const opts = [{ label: "Padrão", value: "nenhum" }];
        if (penultimoTurno) {
            opts.push({ label: `Penúltimo turno (${penultimoTurno.turma})`, value: "penultimo" });
        }
        if (ultimoTurno) {
            opts.push({ label: `Último turno (${ultimoTurno.turma})`, value: "ultimo" });
        }
        return opts;
    }, [penultimoTurno, ultimoTurno]);

    return (
        <Panel header="Quebras por dia por artigo" toggleable collapsed={true}>
            <style>{`
                .qda-wrap { overflow-x: auto; }
                .qda-wrap.qda-print { overflow: visible; }
                .qda-table, .qda-table * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
                .qda-table { border-collapse: collapse; font-size: 12px; width: max-content; }
                .qda-table th, .qda-table td { border: 1px solid #000; padding: 2px 6px; text-align: center; white-space: nowrap; min-width: 34px; }
                .qda-table th { background: #fff; }
                .qda-table .qda-tear, .qda-table .qda-artigo { font-weight: 700; background: #fff; }
                .qda-table .qda-tear { min-width: 50px; }
                .qda-table .qda-artigo { min-width: 64px; }
                .qda-ok { background: #ffc7ce; color: #9c0006; }
                .qda-plain { color: #000; }
                .qda-tear-top5 { background: #c00000 !important; color: #ffffff !important; font-weight: 800 !important; }
                .qda-artigo-top5 { background: #fff0f0 !important; color: #9c0006 !important; font-weight: 700 !important; }
                .qda-row-top5 { background: #fff5f5; }
                .qda-cel-top5 { background: #ffc7ce !important; color: #9c0006 !important; font-weight: 800 !important; outline: 2px solid #c00000; outline-offset: -2px; }
            `}</style>

            <div className="p-fluid p-formgrid p-grid p-mb-2">
                <div className="p-field p-col-12 p-md-4">
                    <label>Artigo</label>
                    <MultiSelect
                        value={artigos}
                        options={artigoOptions}
                        onChange={(e) => setArtigos(e.value)}
                        placeholder="Todos os artigos"
                        display="chip"
                        filter
                    />
                </div>
                <div className="p-field p-col-12 p-md-4">
                    <label>Quebras de trama</label>
                    <div className="p-d-flex p-ai-center">
                        <SelectButton value={tramaOp} options={OPERADORES} onChange={(e) => e.value && setTramaOp(e.value)} unselectable={false} />
                        <InputNumber
                            className="p-ml-2"
                            value={tramaLimite}
                            onValueChange={(e) => setTramaLimite(e.value)}
                            placeholder="Sem filtro"
                            min={0}
                            showButtons
                        />
                    </div>
                </div>
                <div className="p-field p-col-12 p-md-4">
                    <label>Quebras de urdume</label>
                    <div className="p-d-flex p-ai-center">
                        <SelectButton value={urdumeOp} options={OPERADORES} onChange={(e) => e.value && setUrdumeOp(e.value)} unselectable={false} />
                        <InputNumber
                            className="p-ml-2"
                            value={urdumeLimite}
                            onValueChange={(e) => setUrdumeLimite(e.value)}
                            placeholder="Sem filtro"
                            min={0}
                            showButtons
                        />
                    </div>
                </div>
            </div>

            <div className="p-d-flex p-ai-center p-jc-between p-flex-wrap p-mb-3">
                <div className="p-d-flex p-ai-center p-mr-4 p-mb-2">
                    <span className="p-mr-2" style={{ fontWeight: 600 }}>Exibir:</span>
                    <SelectButton value={exibir} options={EXIBIR} onChange={(e) => e.value && setExibir(e.value)} unselectable={false} />
                </div>
                <div className="p-d-flex p-ai-center p-mr-4 p-mb-2">
                    <Checkbox inputId="qda-vazios" checked={ocultarVazios} onChange={(e) => setOcultarVazios(e.checked)} />
                    <label htmlFor="qda-vazios" className="p-ml-2" style={{ cursor: "pointer" }}>Ocultar teares e dias sem ocorrência</label>
                </div>
                <div className="p-d-flex p-ai-center p-mb-2">
                    <Button
                        className="p-button-outlined p-mr-2"
                        label="Limpar filtros"
                        icon="pi pi-filter-slash"
                        onClick={limpar}
                        style={{ width: "auto", whiteSpace: "nowrap" }}
                    />
                    <Button
                        label="Imprimir"
                        icon="pi pi-print"
                        onClick={handlePrint}
                        style={{ width: "auto", whiteSpace: "nowrap" }}
                    />
                </div>
            </div>

            <div ref={tabelaRef} style={{ padding: printing ? "20px" : 0 }}>
                {printing && (
                    <h2 style={{ textAlign: "center", marginBottom: 16 }}>
                        Quebras por dia por artigo — {data?.periodo}
                    </h2>
                )}

                {linhas.length === 0 ? (
                    <p>Nenhum tear encontrado com os filtros selecionados.</p>
                ) : (
                    <>
                        <div className={`qda-wrap ${printing ? "qda-print" : ""}`}>
                            <table className="qda-table">
                                <thead>
                                    <tr>
                                        <th rowSpan={2} className="qda-tear">Tear</th>
                                        <th rowSpan={2} className="qda-artigo">Artigo</th>
                                        {colunas.map((col) => (
                                            <th key={col.ts} colSpan={col.turmas.length}>{rotuloData(col.ts)}</th>
                                        ))}
                                    </tr>
                                    <tr>
                                        {colunas.map((col) =>
                                            col.turmas.map((t) => {
                                                const chave = `${col.ts}|${t}`;
                                                const isUltima = ultimoTurno && chave === ultimoTurno.chave;
                                                const isPenultima = penultimoTurno && chave === penultimoTurno.chave;
                                                const isAtiva = (isUltima && turnoOrdenado === "ultimo") || (isPenultima && turnoOrdenado === "penultimo");
                                                const isClicavel = isUltima || isPenultima;
                                                const handleClick = isUltima
                                                    ? () => setTurnoOrdenado((prev) => (prev === "ultimo" ? null : "ultimo"))
                                                    : isPenultima
                                                    ? () => setTurnoOrdenado((prev) => (prev === "penultimo" ? null : "penultimo"))
                                                    : undefined;

                                                return (
                                                    <th
                                                        key={chave}
                                                        onClick={handleClick}
                                                        style={{
                                                            background: isAtiva ? "#c00000" : (coresTurma[t] || "#ddd"),
                                                            color: isAtiva ? "#fff" : "#000",
                                                            cursor: isClicavel ? "pointer" : "default",
                                                            userSelect: isClicavel ? "none" : "auto",
                                                            fontWeight: 700,
                                                        }}
                                                        title={
                                                            isUltima
                                                                ? "Clique para classificar pelo último turno (Top 5 em vermelho)"
                                                                : isPenultima
                                                                ? "Clique para classificar pelo penúltimo turno (Top 5 em vermelho)"
                                                                : undefined
                                                        }
                                                    >
                                                        <div style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: "3px" }}>
                                                            <span>{t}</span>
                                                            {isClicavel && (
                                                                <i
                                                                    className={isAtiva ? "pi pi-sort-amount-down" : "pi pi-sort-alt"}
                                                                    style={{ fontSize: "10px", opacity: isAtiva ? 1 : 0.5 }}
                                                                />
                                                            )}
                                                        </div>
                                                    </th>
                                                );
                                            })
                                        )}
                                    </tr>
                                </thead>
                                <tbody>
                                    {linhas.map((l) => {
                                        const isTop5 = Boolean(turnoAlvo) && top5Teares.has(l.tear);
                                        return (
                                            <tr key={`${l.tear}|${l.artigo}`} className={isTop5 ? "qda-row-top5" : ""}>
                                                {l.primeiroDoTear && (
                                                    <td
                                                        rowSpan={l.rowSpan}
                                                        className={`qda-tear ${isTop5 ? "qda-tear-top5" : ""}`}
                                                    >
                                                        {l.tear}
                                                    </td>
                                                )}
                                                <td className={`qda-artigo ${isTop5 ? "qda-artigo-top5" : ""}`}>{l.artigo}</td>
                                                {colunas.map((col) =>
                                                    col.turmas.map((t) => {
                                                        const chave = `${col.ts}|${t}`;
                                                        const isAlvo = turnoAlvo && chave === turnoAlvo.chave;
                                                        const c = l.celulas[chave];
                                                        const mostra = c && c.ok;
                                                        return (
                                                            <td
                                                                key={chave}
                                                                className={
                                                                    isAlvo && isTop5 && mostra
                                                                        ? "qda-cel-top5"
                                                                        : mostra
                                                                        ? (filtroAtivo ? "qda-ok" : "qda-plain")
                                                                        : ""
                                                                }
                                                                title={mostra ? `Trama: ${c.trama} | Urdume: ${c.urdume}` : undefined}
                                                            >
                                                                {renderCelula(c)}
                                                            </td>
                                                        );
                                                    })
                                                )}
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                        {turnoAlvo && top5Teares.size > 0 && (
                            <div style={{ fontSize: 12, color: "#c00000", marginTop: 8, fontWeight: 600 }}>
                                * Tabela classificada pelo {turnoOrdenado === "penultimo" ? "penúltimo" : "último"} turno ({turnoAlvo.rotulo}) em ordem decrescente de quebras. Os {top5Teares.size} teares com maiores quebras estão destacados em vermelho.
                            </div>
                        )}
                    </>
                )}
            </div>
        </Panel>
    );
});